/**
 * 导量离线支付页（leadCharge）埋点 Mixin
 *
 * 负责支付确认页、礼包码页的用户行为上报，通过 burnReport 接口投递。
 * 页面组件需提供：orderInfo、leadId、orderToken、currentAppkey、wxAppId、
 * isInWechat、selectedPayType、currentPayType 等数据/计算属性。
 */
import { burnReport } from '@/api/pay';
import { API_URL, extra_data } from '@/config';
import { sort_ASCII } from '@/libs/utils';

const EVENT_TRACK_URL = `${API_URL}/noncore/event-track/extra`;
const EXIT_REASON = '离开页面';

/** 支付页埋点事件名常量 */
const PAYMENT_TRACK_EVENTS = {
  /** 支付确认页曝光 */
  EXPOSE: 'PAYMENT_CONFIRM_PAGE_EXPOSE',
  /** 支付确认页取消/退出（未点击立即支付即离开） */
  CANCEL_EXIT: 'PAYMENT_CANCEL_EXIT',
  /** 点击「立即支付」确认按钮 */
  CONFIRM_CLICK: 'PAYMENT_CONFIRM_CLICK',
  /** 点击「联系客服」链接 */
  CUSTOMER_SERVICE_CLICK: 'CUSTOMER_SERVICE_CLICK',
  /** 点击「复制礼包码」按钮 */
  GIFT_CODE_COPY_CLICK: 'GIFT_CODE_COPY_CLICK',
  /** 礼包码领取成功页曝光 */
  GIFT_CODE_PAGE_VIEW: 'GIFT_CODE_PAGE_VIEW',
  /** 礼包码页退出 */
  GIFT_CODE_PAGE_EXIT: 'GIFT_CODE_PAGE_EXIT'
};

export default {
  data() {
    return {
      /** 进入页面时刻戳，用于计算 page_stay_duration */
      pageEnterTime: Date.now(),
      /** 是否已上报支付确认页曝光，防止重复 */
      hasReportedExpose: false,
      /** 是否已上报支付确认页退出，防止重复 */
      hasReportedExit: false,
      /** 是否正在跳转第三方支付页，跳转期间不上报退出埋点 */
      isPayRedirecting: false,
      /** 联系客服点击次数（礼包码页退出埋点会带上） */
      customerServiceClickCount: 0,
      /** 复制礼包码点击次数（礼包码页退出/复制埋点会带上） */
      giftCodeCopyClickCount: 0,
      /** 是否已上报礼包码页曝光 */
      hasReportedGiftCodePageView: false,
      /** 是否已上报礼包码页退出 */
      hasReportedGiftCodePageExit: false
    };
  },

  computed: {
    paymentTrackPayStatus() {
      return Number(this.orderInfo.pay_status);
    },

    paymentTrackGiftStatus() {
      return Number(this.orderInfo.gift_status);
    },

    paymentTrackOrderStatus() {
      return Number(this.orderInfo.status);
    },

    /**
     * 当前是否处于「支付确认页」
     * 条件：未支付成功。订单未加载完成时也按未支付处理，确保退出可上报。
     */
    isOnPaymentConfirmPage() {
      console.log('isOnPaymentConfirmPage', this.paymentTrackPayStatus);
      return this.paymentTrackPayStatus !== 1;
    },

    /**
     * 当前是否处于「礼包/支付成功相关页」
     * 与 leadCharge.vue 的 pay_status === 1 展示分支保持一致，避免退出时被 CANCEL_EXIT 抢占。
     */
    isOnGiftCodePage() {
      return this.paymentTrackPayStatus === 1;
    }
  },

  beforeRouteLeave(to, from, next) {
    this.reportPageExit(EXIT_REASON);
    next();
  },

  mounted() {
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('popstate', this.handlePopState);
    if (window.navigation && window.navigation.addEventListener) {
      window.navigation.addEventListener('navigate', this.handleNavigate);
    }
  },

  beforeDestroy() {
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('popstate', this.handlePopState);
    if (window.navigation && window.navigation.removeEventListener) {
      window.navigation.removeEventListener('navigate', this.handleNavigate);
    }
  },

  methods: {
    /**
     * 从 localStorage 读取当前登录用户信息
     * @returns {Object} 用户信息对象，解析失败或不存在时返回空对象
     */
    getLocalUserInfo() {
      try {
        return JSON.parse(localStorage.getItem('userInfo')) || {};
      } catch (error) {
        return {};
      }
    },

    /**
     * 计算用户在本页的停留时长（秒）
     * @returns {number} 自 pageEnterTime 起至当前的秒数（四舍五入）
     */
    getPageStayDuration() {
      return Math.round((Date.now() - this.pageEnterTime) / 1000);
    },

    /**
     * 获取当前选中支付方式的埋点展示名称
     * @returns {string} wechat_jsapi | wechat | alipay
     */
    getPayTypeName() {
      if (this.isInWechat) return 'wechat_jsapi';
      return this.selectedPayType === 'wechat' ? 'wechat' : 'alipay';
    },

    /**
     * 获取支付方式对应的埋点状态码
     * @returns {string} '1' 微信，'2' 支付宝
     */
    getPayStatusCode() {
      if (this.isInWechat || this.selectedPayType === 'wechat') return '1';
      return '2';
    },

    /**
     * 组装客户端环境扩展字段（client_extra_data）
     * 微信内会将 android_sdk_level 标记为 WeChat
     * @returns {Object} 客户端设备、系统、IP 等信息
     */
    getClientExtraData() {
      const os = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'ios' : 'android';
      return Object.assign({}, extra_data, {
        android_sdk_level: this.isInWechat ? 'WeChat' : extra_data.android_sdk_level,
        device_id: extra_data.device_id,
        device_name: extra_data.device_name,
        imei: extra_data.imei,
        ip_addr: extra_data.ip_addr,
        os,
        match_device: '',
        ad_data: '{}'
      });
    },

    /**
     * 组装用户维度埋点字段（openid、unionid、appkey 等）
     * @returns {Object} 用户与小程序/规则配置相关字段
     */
    getUserTrackExtraData() {
      const userInfo = this.getLocalUserInfo();
      const orderInfo = this.orderInfo || {};
      return {
        appid: this.wxAppId,
        openid: userInfo.open_id || userInfo.openid,
        unionid: userInfo.real_union_id || userInfo.unionid || userInfo.union_id,
        user_id: userInfo.user_id || orderInfo.user_id,
        mini_game_appkey: this.currentAppkey,
        mini_game_appid: orderInfo.mini_game_appid || this.$route.query.mini_game_appid || this.wxAppId,
        rule_config_id: orderInfo.rule_config_id || this.$route.query.rule_config_id
      };
    },

    /**
     * 组装订单维度埋点字段
     * @returns {Object} 订单、游戏、区服、角色、商品、金额、支付方式等
     */
    getOrderTrackData() {
      const orderInfo = this.orderInfo || {};
      return {
        lead_id: this.leadId,
        order_token: this.orderToken,
        order_id: orderInfo.order_id || orderInfo.order_sn || orderInfo.order_no || orderInfo.id,
        game_id: orderInfo.game_id || orderInfo.app_id || this.currentAppkey,
        game_name: orderInfo.game,
        server_id: orderInfo.server_id || orderInfo.server_num,
        server_name: orderInfo.server_name,
        role_id: orderInfo.role_id,
        role_name: orderInfo.role_name,
        product_id: orderInfo.product_id || orderInfo.item_id || orderInfo.goods_id,
        product_name: orderInfo.product_name,
        product_count: orderInfo.product_count || orderInfo.count || orderInfo.num || orderInfo.quantity,
        amount: orderInfo.money,
        pay_type: this.currentPayType,
        pay_type_name: this.getPayTypeName()
      };
    },

    /**
     * 礼包码页专用埋点字段（订单号、礼包码）
     * @returns {Object} order_id、gift_code
     */
    getGiftPageTrackExtraData() {
      const orderInfo = this.orderInfo || {};
      return {
        order_id: orderInfo.order_id || orderInfo.order_sn || orderInfo.order_no || orderInfo.id,
        gift_code: orderInfo.gift_code
      };
    },

    /**
     * 埋点字段转字符串，undefined / null 转为空字符串
     * @param {*} value 原始值
     * @returns {string}
     */
    toTrackString(value) {
      if (value === undefined || value === null) return '';
      return String(value);
    },

    /**
     * 将对象各字段规范为字符串，缺失值统一为 ''
     * @param {Object} data 原始对象
     * @returns {Object}
     */
    normalizeTrackFields(data) {
      const result = {};
      Object.keys(data).forEach(key => {
        result[key] = this.toTrackString(data[key]);
      });
      return result;
    },

    /**
     * 联系客服点击（CUSTOMER_SERVICE_CLICK）专用 extra_data
     * 覆盖需求文档：基本信息、用户信息、小游戏信息、礼包码页信息、点击次数
     * @returns {Object} 各字段缺失时为 ''
     */
    getCustomerServiceTrackExtraData() {
      const userInfo = this.getLocalUserInfo();
      const orderInfo = this.orderInfo || {};
      const clientExtraData = this.getClientExtraData();
      const userTrack = this.getUserTrackExtraData();
      const giftPage = this.getGiftPageTrackExtraData();

      return this.normalizeTrackFields({
        os: clientExtraData.os,
        device_name: clientExtraData.device_name,
        device_id: clientExtraData.device_id,
        system_version: clientExtraData.android_version || clientExtraData.system_version,
        ip_addr: clientExtraData.ip_addr,
        user_id: userInfo.user_id || orderInfo.user_id,
        openid: userTrack.openid,
        unionid: userTrack.unionid,
        mini_game_appkey: userTrack.mini_game_appkey,
        mini_game_appid: userTrack.mini_game_appid,
        order_id: giftPage.order_id,
        gift_code: giftPage.gift_code,
        click_count: this.customerServiceClickCount
      });
    },

    /**
     * 过滤对象中的空值（undefined / null / 空字符串）
     * @param {Object} data 原始对象
     * @returns {Object} 仅包含有效字段的新对象
     */
    removeEmpty(data) {
      const result = {};
      Object.keys(data).forEach(key => {
        const value = data[key];
        if (value !== undefined && value !== null && value !== '') {
          result[key] = value;
        }
      });
      return result;
    },

    /**
     * 给退出场景构造可脱离页面生命周期发送的埋点请求。
     * 普通 axios 在 pagehide/unload 阶段容易被浏览器取消，优先用 sendBeacon。
     * @param {Object} payload 未签名的埋点参数
     * @returns {Object} 已签名的埋点参数
     */
    getSignedTrackPayload(payload) {
      return Object.assign({}, payload, {
        sign: sort_ASCII(payload)
      });
    },

    /**
     * 发送埋点。页面退出时使用 fetch keepalive/sendBeacon，其他场景沿用 axios。
     * @param {Object} payload 未签名的埋点参数
     * @param {Object} [options={}] 配置项
     * @param {boolean} [options.useBeacon=false] 是否使用离页可靠发送
     */
    sendTrackPayload(payload, options = {}) {
      if (options.useBeacon) {
        const signedPayload = this.getSignedTrackPayload(payload);
        const body = JSON.stringify(signedPayload);

        if (window.fetch) {
          try {
            window.fetch(EVENT_TRACK_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body,
              keepalive: true
            }).catch(() => {});
            return;
          } catch (error) {}
        }

        try {
          if (navigator.sendBeacon) {
            const blob = new Blob([body], { type: 'application/json' });
            if (navigator.sendBeacon(EVENT_TRACK_URL, blob)) return;
          }
        } catch (error) {}
      }

      burnReport(payload).catch(() => {});
    },

    /**
     * 统一支付页埋点上报入口
     * @param {string} event 事件名，见 PAYMENT_TRACK_EVENTS
     * @param {Object} [extra={}] 追加到 extra_data 的自定义字段
     * @param {Object} [options={}] 配置项
     * @param {boolean} [options.onlyUserFields=false]
     *   为 true 时 extra_data 仅含用户字段 + extra，不含订单/设备/停留时长（用于部分点击、退出事件）
     * @param {Function|Object} [options.preserveEmptyExtra]
     *   传入函数或对象时，extra_data 使用 normalizeTrackFields 保留空字符串，不再 removeEmpty
     */
    reportPaymentEvent(event, extra = {}, options = {}) {
      const userInfo = this.getLocalUserInfo();
      const clientExtraData = this.getClientExtraData();
      let trackExtraData;
      if (options.preserveEmptyExtra) {
        trackExtraData = typeof options.preserveEmptyExtra === 'function'
          ? options.preserveEmptyExtra()
          : this.normalizeTrackFields(options.preserveEmptyExtra);
      } else if (options.onlyUserFields) {
        trackExtraData = this.removeEmpty(Object.assign({}, this.getUserTrackExtraData(), extra));
      } else {
        trackExtraData = this.removeEmpty(Object.assign({}, this.getUserTrackExtraData(), this.getOrderTrackData(), {
          os: clientExtraData.os,
          device_id: clientExtraData.device_id,
          device_name: clientExtraData.device_name,
          ip_addr: clientExtraData.ip_addr,
          system_version: clientExtraData.android_version || clientExtraData.system_version,
          page_stay_duration: this.getPageStayDuration(),
          stay_duration: this.getPageStayDuration()
        }, extra));
      }

      const payload = options.preserveEmptyExtra
        ? {
          appkey: this.toTrackString(this.currentAppkey),
          event,
          device_id: this.toTrackString(clientExtraData.device_id),
          device_name: this.toTrackString(clientExtraData.device_name),
          system_version: this.toTrackString(clientExtraData.android_version || clientExtraData.system_version),
          os: this.toTrackString(clientExtraData.os),
          user_id: this.toTrackString(userInfo.user_id || this.orderInfo.user_id),
          extra_data: trackExtraData,
          client_extra_data: JSON.stringify(clientExtraData)
        }
        : this.removeEmpty({
          appkey: this.currentAppkey,
          event,
          device_id: clientExtraData.device_id,
          device_name: clientExtraData.device_name,
          system_version: clientExtraData.android_version || clientExtraData.system_version,
          os: clientExtraData.os,
          user_id: userInfo.user_id || this.orderInfo.user_id,
          extra_data: trackExtraData,
          client_extra_data: JSON.stringify(clientExtraData)
        });

      this.sendTrackPayload(payload, {
        useBeacon: options.useBeacon
      });
    },

    /**
     * 上报支付确认页曝光（PAYMENT_CONFIRM_PAGE_EXPOSE）
     * 仅在未支付且订单有效时上报一次
     */
    reportExpose() {
      if (this.hasReportedExpose || this.paymentTrackPayStatus !== 0 || this.paymentTrackOrderStatus === 0) return;
      this.hasReportedExpose = true;
      this.reportPaymentEvent(PAYMENT_TRACK_EVENTS.EXPOSE);
    },

    /**
     * 上报支付确认页取消退出（PAYMENT_CANCEL_EXIT）
     * 只要当前支付页未支付成功且未重复上报，即记录退出。
     * @param {string} exitReason 退出原因：离开页面
     */
    reportCancelExit(exitReason, options = {}) {
      if (this.hasReportedExit || this.paymentTrackPayStatus === 1) return;
      this.hasReportedExit = true;
      this.reportPaymentEvent(PAYMENT_TRACK_EVENTS.CANCEL_EXIT, Object.assign({}, this.getOrderTrackData(), {
        exit_reason: exitReason
      }), Object.assign({}, options, { onlyUserFields: true }));
    },

    /**
     * 上报礼包码页退出（GIFT_CODE_PAGE_EXIT）
     * @param {string} exitReason 退出原因：离开页面
     */
    reportGiftCodePageExit(exitReason, options = {}) {
      if (this.hasReportedGiftCodePageExit) return;
      this.hasReportedGiftCodePageExit = true;
      this.reportPaymentEvent(PAYMENT_TRACK_EVENTS.GIFT_CODE_PAGE_EXIT, Object.assign(
        {},
        this.getGiftPageTrackExtraData(),
        {
          click_count: this.giftCodeCopyClickCount,
          exit_reason: exitReason
        }
      ), Object.assign({}, options, { onlyUserFields: true }));
    },

    /**
     * 根据当前页面类型分发退出埋点
     * @param {string} exitReason 退出原因：离开页面
     */
    reportPageExit(exitReason, options = {}) {
      if (this.isOnGiftCodePage) {
        this.reportGiftCodePageExit(exitReason, options);
      } else if (this.isOnPaymentConfirmPage) {
        this.reportCancelExit(exitReason, options);
      }
    },

    /**
     * 上报点击「立即支付」（PAYMENT_CONFIRM_CLICK）
     * extra_data 需包含：用户信息、订单/支付信息（order_id、game_id、区服、角色、商品等）、
     * 设备环境（os、device_id 等）、页面停留时长，以及支付方式 pay_status（1 微信 / 2 支付宝）
     */
    reportConfirmClick() {
      this.reportPaymentEvent(PAYMENT_TRACK_EVENTS.CONFIRM_CLICK, {
        pay_status: this.getPayStatusCode()
      });
    },

    /**
     * 联系客服链接点击：累加次数并上报 CUSTOMER_SERVICE_CLICK
     */
    handleCustomerServiceClick() {
      this.customerServiceClickCount += 1;
      this.reportCustomerServiceClick();
    },

    /**
     * 上报联系客服点击（CUSTOMER_SERVICE_CLICK）
     * extra_data：基本信息、用户信息、小游戏信息、礼包码页信息（order_id/gift_code）、点击次数；
     * 缺失字段传空字符串
     */
    reportCustomerServiceClick() {
      this.reportPaymentEvent(
        PAYMENT_TRACK_EVENTS.CUSTOMER_SERVICE_CLICK,
        {},
        { preserveEmptyExtra: () => this.getCustomerServiceTrackExtraData() }
      );
    },

    /**
     * 上报复制礼包码点击（GIFT_CODE_COPY_CLICK）
     * 调用前需自行累加 giftCodeCopyClickCount
     */
    reportGiftCodeCopyClick() {
      this.reportPaymentEvent(PAYMENT_TRACK_EVENTS.GIFT_CODE_COPY_CLICK, Object.assign(
        {},
        this.getGiftPageTrackExtraData(),
        { click_count: this.giftCodeCopyClickCount }
      ), { onlyUserFields: true });
    },

    /**
     * 上报礼包码领取成功页曝光（GIFT_CODE_PAGE_VIEW）
     * 条件：已支付、礼包状态为已领取且有礼包码，仅上报一次
     */
    reportGiftCodePageView() {
      if (this.hasReportedGiftCodePageView) return;
      if (this.paymentTrackPayStatus !== 1) return;
      if (this.paymentTrackGiftStatus !== 2 || !this.orderInfo.gift_code) return;
      this.hasReportedGiftCodePageView = true;
      this.reportPaymentEvent(
        PAYMENT_TRACK_EVENTS.GIFT_CODE_PAGE_VIEW,
        this.getGiftPageTrackExtraData(),
        { onlyUserFields: true }
      );
    },

    /**
     * 在 DOM 更新后尝试上报礼包码页曝光（订单异步刷新后视图才展示礼包码块）
     */
    tryReportGiftCodePageView() {
      this.$nextTick(() => {
        this.reportGiftCodePageView();
      });
    },

    /**
     * 浏览器 pagehide 时上报页面退出。
     */
    handlePageHide() {
      this.reportPageExit(EXIT_REASON, { useBeacon: true });
    },

    /**
     * beforeunload 作为 pagehide 的兜底，覆盖部分浏览器关闭/返回不触发 pagehide 的情况。
     */
    handleBeforeUnload() {
      this.reportPageExit(EXIT_REASON, { useBeacon: true });
    },

    /**
     * 浏览器前进/后退时也尝试即时上报，退出原因统一为离开页面。
     */
    handlePopState() {
      this.reportPageExit(EXIT_REASON, { useBeacon: true });
    },

    /**
     * Chrome 的 Navigation API 对返回/前进做即时上报，退出原因统一为离开页面。
     */
    handleNavigate(event) {
      if (event.navigationType === 'traverse') {
        this.reportPageExit(EXIT_REASON, { useBeacon: true });
      }
    },

    /**
     * 从第三方支付页返回时，页面可能通过 BFCache 恢复，需重置跳转态。
     */
    handlePageShow() {
      if (!this.isPayRedirecting) return;
      this.isPayRedirecting = false;
      if (typeof this.getOrderInfo === 'function') {
        this.getOrderInfo();
      }
    }
  }
};
